import { ChangeDetectionStrategy, Component, computed, DOCUMENT, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import {
  AtFooterComponent,
  AtSpinnerComponent,
  BREAKPOINTS,
  ConfigurableAuthService,
  FooterLink,
  matchMediaQuery,
} from '@anothertrip/anothertrip-ds';
import { filter, map, shareReplay, startWith } from 'rxjs';

import { DashboardSidebarComponent } from './components/dashboard-sidebar/dashboard-sidebar.component';
import { HeaderWrapperComponent } from './components/header-wrapper/header-wrapper.component';
import { MapJobNoticesComponent } from './components/map-job-notices/map-job-notices.component';
import { SkipLinkComponent } from './components/skip-link/skip-link.component';
import { CookieSettingsLinksDirective } from './directives/cookie-settings-links/cookie-settings-links.directive';
import { ConsoleShellService } from './services/console-shell/console-shell.service';
import { COOKIE_SETTINGS_HREF } from './services/cookie-consent/cookie-consent.service';
import { MapJobsService } from './services/map-jobs.service';

export type PageType =
  | 'map'
  | 'dashboard'
  | 'get-started'
  | 'default'
  | 'subscription-processing'
  | 'subscription-complete';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    HeaderWrapperComponent,
    DashboardSidebarComponent,
    SkipLinkComponent,
    AtFooterComponent,
    AtSpinnerComponent,
    MapJobNoticesComponent,
    CookieSettingsLinksDirective,
  ],
  templateUrl: './app.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  private readonly router = inject(Router);
  private readonly authService = inject(ConfigurableAuthService);
  private readonly body = inject(DOCUMENT).body;
  private readonly shell = inject(ConsoleShellService);
  private readonly mapJobs = inject(MapJobsService);

  protected readonly pageType = toSignal<PageType>(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => this.getPageTypeFromUrl(e.urlAfterRedirects)),
      startWith(this.getPageTypeFromUrl(window.location.href)),
      shareReplay({ bufferSize: 1, refCount: true })
    )
  );

  protected readonly isUIVisible = computed<boolean>(
    () =>
      this.pageType() !== 'map' &&
      this.pageType() !== 'subscription-processing' &&
      this.pageType() !== 'subscription-complete'
  );

  private readonly isDesktop = matchMediaQuery(`(min-width: ${BREAKPOINTS.XL + 1}px)`);

  /** The sidebar is standing: it is the app's left edge, and the footer must start where it ends. */
  protected readonly isSidebarVisible = computed<boolean>(() => this.shell.hasSidebar() && this.isDesktop());

  /**
   * The console has no top bar. On a desktop dashboard the sidebar carries everything the header
   * used to — the brand at the top, the profile at the bottom — so a second strip of chrome above
   * the work surface is just a band of nothing.
   *
   * The header stays everywhere else: on mobile it holds the only way to reach the nav drawer, and
   * on signed-out pages it holds the sign-in link.
   */
  protected readonly isHeaderVisible = computed<boolean>(() => this.isUIVisible() && !this.isSidebarVisible());

  protected readonly footerLinks: FooterLink[] = [
    { label: 'Blog', href: 'https://content.anothertrip.com/blog' },
    { label: 'Terms of use', routerLink: '/terms-of-use' },
    { label: 'Privacy policy', routerLink: '/privacy-policy' },
    // Beside the privacy policy, where the consent kit's dialog says it is. It opens that dialog rather
    // than going anywhere: `appCookieSettingsLinks` on the footer answers the click (AT-2749).
    { label: 'Cookie settings', href: COOKIE_SETTINGS_HREF },
  ];

  protected readonly isLoggingOut = this.authService.isLoggingOut;

  constructor() {
    effect(() => this.handleLogout(this.isLoggingOut()));
  }

  private getPageTypeFromUrl(url: string): PageType {
    if (url.startsWith('/') && url.length === 1) return 'get-started';
    if (url.includes('/dashboard')) return 'dashboard';
    if (url.includes('/map')) return 'map';
    if (url.includes('/businesses-map')) return 'map';
    if (url.includes('/get-started')) return 'get-started';
    if (url.includes('/subscription/processing')) return 'subscription-processing';
    if (url.includes('/subscription/complete')) return 'subscription-complete';
    return 'default';
  }

  private handleLogout(isLoggingOut: boolean): void {
    if (isLoggingOut) {
      // The next account must not be told about, or keep polling for, the last one's maps (AT-2685).
      this.mapJobs.cancelAll();
      this.body.style.overflow = 'hidden';
      this.body.dataset['logoutScrollLock'] = 'true';
    } else {
      this.body.style.overflow = '';
      delete this.body.dataset['logoutScrollLock'];
    }
  }
}
